/**
 * Dziennik sesji kiosku (ForClever 2026-10-27: „kto, kiedy i jak długo
 * korzystał z CRM-u").
 *
 * Zapis jest w Firmaticu, w tabeli Journal (bez zmiany schematu):
 *  - 2647 z `session_src=kiosk` otwiera sesję i oddaje jej numer w `sv_session`,
 *    a nieudane logowanie zapisuje jako osobny wpis;
 *  - 5305 `touch` — „użytkownik pracuje" (co najwyżej co 5 min, tylko gdy karta
 *    jest widoczna i ktoś jej dotknął od ostatniego razu);
 *  - 5305 `end` — wylogowanie albo wygaśnięcie 8-godzinnej sesji;
 *  - 5305 `list` — odczyt dla Pulpitu zarządczego (KiS 5303).
 *
 * Zamknięcie karty czy ubicie apki nie wyśle `end`, dlatego sesja bez
 * aktywności dłużej niż IDLE_AFTER_MS liczy się jako zakończona na ostatniej
 * aktywności. Czas trwania = od zalogowania do końca albo ostatniej aktywności,
 * więc dokładność to ok. TOUCH_INTERVAL_MS.
 *
 * Zapisy są „wyślij i zapomnij": dziennik nigdy nie blokuje pracy w kiosku,
 * a backend bez 5305 po prostu nie dostaje pingów (brak sv_session z 2647).
 */
import { apiClient } from './api.client';
import { extractValue } from './route-planning.service';
import { isAccessDenied } from '../utils/authz';

export type SessionEndReason = 'logout' | 'expired';

export const TOUCH_INTERVAL_MS = 5 * 60 * 1000;
export const IDLE_AFTER_MS = 15 * 60 * 1000;

export async function touchSession(userId: string, sessionId: string): Promise<void> {
  try {
    await apiClient.execute('5305', { sv_numrecuser: userId, action: 'touch', session: sessionId });
  } catch {
    // dziennik nie może przeszkadzać w pracy
  }
}

export async function endSession(userId: string, sessionId: string, reason: SessionEndReason): Promise<void> {
  try {
    await apiClient.execute('5305', { sv_numrecuser: userId, action: 'end', session: sessionId, reason });
  } catch {
    // jw.
  }
}

/**
 * Pingi aktywności dla bieżącej sesji. `getSession` czytany przy każdym
 * pingu — po wylogowaniu zwraca null i pingi ustają same.
 * Zwraca funkcję zatrzymującą.
 */
export function startSessionHeartbeat(
  getSession: () => { userId: string; sessionId: string } | null,
): () => void {
  let interacted = false;
  let lastTouch = Date.now(); // zalogowanie samo ustawia ostatnią aktywność

  const onInteract = () => { interacted = true; };
  const tick = () => {
    if (document.visibilityState !== 'visible' || !interacted) return;
    if (Date.now() - lastTouch < TOUCH_INTERVAL_MS) return;
    const s = getSession();
    if (!s) return;
    interacted = false;
    lastTouch = Date.now();
    void touchSession(s.userId, s.sessionId);
  };

  const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
  events.forEach((e) => window.addEventListener(e, onInteract, { passive: true, capture: true }));
  document.addEventListener('visibilitychange', tick);
  const timer = window.setInterval(tick, 60 * 1000);

  return () => {
    events.forEach((e) => window.removeEventListener(e, onInteract, { capture: true }));
    document.removeEventListener('visibilitychange', tick);
    window.clearInterval(timer);
  };
}

// ---------------------------------------------------------------------------
// Odczyt (Pulpit zarządczy)

export interface SessionRow {
  kind: 'session' | 'failed';
  numrecuser: string;
  userident: string;
  username: string;
  /** Wpisany login — tylko przy nieudanym logowaniu. */
  login: string;
  start: Date | null;
  last: Date | null;
  end: Date | null;
  reason: string;
}

export interface SessionLog {
  now: Date;
  rows: SessionRow[];
}

/** RRRRMMDDGGMMSS (czas serwera) → Date; pusty/zły → null. */
export function parseTs(ts: string): Date | null {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec((ts || '').trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s] = m.map(Number);
  return new Date(y!, mo! - 1, d!, h!, mi!, s!);
}

export function parseSessionRows(rows: string[]): SessionRow[] {
  return rows
    .filter((xml): xml is string => typeof xml === 'string')
    .map((xml) => ({
      kind: extractValue(xml, 'kind') === 'failed' ? 'failed' as const : 'session' as const,
      numrecuser: extractValue(xml, 'numrecuser').trim(),
      userident: extractValue(xml, 'userident').trim(),
      username: extractValue(xml, 'username').trim(),
      login: extractValue(xml, 'login').trim(),
      start: parseTs(extractValue(xml, 'start')),
      last: parseTs(extractValue(xml, 'last')),
      end: parseTs(extractValue(xml, 'end')),
      reason: extractValue(xml, 'reason').trim(),
    }));
}

/** `null` = brak dostępu; rzuca przy błędzie technicznym. */
export async function loadSessionLog(viewerId: string, dateFrom: string, dateTo: string): Promise<SessionLog | null> {
  const response = await apiClient.execute('5305', {
    sv_numrecuser: viewerId,
    action: 'list',
    date_from: dateFrom,
    date_to: dateTo,
  });
  if (isAccessDenied(response)) return null;
  if (!response?.success) throw new Error(response?.error || 'Błąd odczytu dziennika sesji');
  const result = response.data?.result;
  const resultXml = typeof result === 'string' ? result : '';
  if (resultXml.includes('flag_ok>no')) {
    throw new Error(extractValue(resultXml, 'error') || 'Błąd odczytu dziennika sesji');
  }
  const linia = response.data?.linia;
  const rows = !linia ? [] : Array.isArray(linia) ? linia : [linia];
  return {
    now: parseTs(String(response.data?.now ?? '')) ?? new Date(),
    rows: parseSessionRows(rows),
  };
}

export type SessionStatus = 'active' | 'idle' | 'logout' | 'expired';

export function sessionStatus(row: SessionRow, now: Date): SessionStatus {
  if (row.end) return row.reason === 'expired' ? 'expired' : 'logout';
  const last = row.last ?? row.start;
  if (last && now.getTime() - last.getTime() <= IDLE_AFTER_MS) return 'active';
  return 'idle';
}

/** Koniec sesji do liczenia czasu: wylogowanie albo ostatnia aktywność. */
export function sessionEnd(row: SessionRow): Date | null {
  return row.end ?? row.last ?? row.start;
}

export function sessionDurationMs(row: SessionRow): number {
  const end = sessionEnd(row);
  if (!row.start || !end) return 0;
  return Math.max(0, end.getTime() - row.start.getTime());
}

export interface UserDaySummary {
  numrecuser: string;
  name: string;
  /** RRRR-MM-DD (dzień zalogowania). */
  day: string;
  sessions: number;
  firstStart: Date;
  lastActivity: Date;
  totalMs: number;
}

const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Sesje zsumowane na osobę i dzień zalogowania; najnowsze dni na górze. */
export function summarizeByUserDay(rows: SessionRow[]): UserDaySummary[] {
  const map = new Map<string, UserDaySummary>();
  for (const r of rows) {
    if (r.kind !== 'session' || !r.start) continue;
    const day = dayKey(r.start);
    const key = `${r.numrecuser}|${day}`;
    const end = sessionEnd(r) ?? r.start;
    const cur = map.get(key);
    if (!cur) {
      map.set(key, {
        numrecuser: r.numrecuser,
        name: r.username || r.userident || `#${r.numrecuser}`,
        day,
        sessions: 1,
        firstStart: r.start,
        lastActivity: end,
        totalMs: sessionDurationMs(r),
      });
    } else {
      cur.sessions += 1;
      if (r.start < cur.firstStart) cur.firstStart = r.start;
      if (end > cur.lastActivity) cur.lastActivity = end;
      cur.totalMs += sessionDurationMs(r);
    }
  }
  return [...map.values()].sort((a, b) =>
    b.day.localeCompare(a.day) || a.name.localeCompare(b.name, 'pl'));
}

/** „2 h 05 min", „12 min", „< 1 min". */
export function formatDuration(ms: number): string {
  const min = Math.floor(ms / 60000);
  if (min < 1) return '< 1 min';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
}

export const FAIL_REASON_LABEL: Record<string, string> = {
  haslo: 'złe hasło',
  nieznany_login: 'nieznany login',
  nie_pracownik: 'konto spoza pracowników',
};
